import {
  Alert,
  type AlertProps,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { StatusChip } from '../../components/StatusChip';
import { initials } from '../../lib/format';
import { errorMessage } from '../../lib/notify';
import { displayStatus, STATUS_LABELS, type DisplayStatus, type Tenant } from './api';

export function TenantStatusChip({ tenant }: { tenant: Pick<Tenant, 'status' | 'subscription'> }) {
  const s = displayStatus(tenant);
  return <StatusChip status={s} label={STATUS_LABELS[s]} />;
}

/** Trial (never paid) or Paid (renewed on a plan or redeemed a code at least once). */
export function BillingChip({ onTrial }: { onTrial: boolean }) {
  return onTrial ? (
    <StatusChip status="TRIAL" label="Trial" />
  ) : (
    <StatusChip status="PAID" label="Paid" />
  );
}

export function StatusChipFor({ status }: { status: DisplayStatus }) {
  return <StatusChip status={status} label={STATUS_LABELS[status]} />;
}

/** Square-ish initials tile for a shop. */
export function TenantAvatar({ name, size = 36 }: { name: string; size?: number }) {
  return (
    <Avatar
      variant="rounded"
      sx={(t) => {
        const p = t.vars?.palette.primary.main ?? t.palette.primary.main;
        return {
          width: size,
          height: size,
          borderRadius: `${String(Math.round(size * 0.3))}px`,
          fontSize: size * 0.38,
          fontWeight: 700,
          color: 'primary.main',
          bgcolor: `color-mix(in srgb, ${p} 12%, transparent)`,
          border: `1px solid color-mix(in srgb, ${p} 18%, transparent)`,
        };
      }}
    >
      {initials(name)}
    </Avatar>
  );
}

/** Responsive grid of label/value fields. */
export function FieldGrid({ children, min = 180 }: { children: ReactNode; min?: number }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.25,
        gridTemplateColumns: {
          xs: '1fr 1fr',
          sm: `repeat(auto-fill, minmax(${String(min)}px, 1fr))`,
        },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * A confirmation that records a reason (suspend, resume, revoke). `onConfirm` gets the trimmed
 * reason; a rejected promise keeps the dialog open and shows the error inside it.
 */
export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel,
  danger,
  reasonLabel = 'Reason',
  reasonRequired = true,
  helper,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  reasonLabel?: string;
  reasonRequired?: boolean;
  helper?: string;
  onConfirm: (reason: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const trimmed = reason.trim();
  const invalid = (reasonRequired && trimmed.length === 0) || trimmed.length > 500;

  const close = () => {
    if (busy) return;
    setReason('');
    setTouched(false);
    setError(null);
    onClose();
  };

  const submit = () => {
    setTouched(true);
    if (invalid) return;
    setBusy(true);
    setError(null);
    onConfirm(trimmed)
      .then(() => {
        setBusy(false);
        setReason('');
        setTouched(false);
        onClose();
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25}>
            <Typography color="text.secondary" component="div">
              {body}
            </Typography>
            {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
            <TextField
              label={reasonRequired ? reasonLabel : `${reasonLabel} (optional)`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              multiline
              minRows={2}
              autoFocus
              required={reasonRequired}
              error={touched && invalid}
              helperText={
                touched && invalid
                  ? trimmed.length > 500
                    ? 'Keep it under 500 characters.'
                    : 'A reason is required. It is saved in the audit log.'
                  : (helper ?? 'Saved in the audit log.')
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color={danger ? 'error' : 'primary'}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * An Alert whose text stays readable in both schemes: tinted background from the severity colour,
 * primary text colour (the standard Alert derives its dark-mode text from palette.*.light, which is
 * a translucent tint in this theme).
 */
export function Callout({
  severity = 'info',
  mt,
  ...props
}: Omit<AlertProps, 'sx'> & { mt?: number }) {
  return (
    <Alert
      severity={severity}
      {...props}
      sx={(t) => {
        const c = (t.vars ?? t).palette[severity].main;
        return {
          mt,
          color: 'text.primary',
          bgcolor: `color-mix(in srgb, ${c} 9%, transparent)`,
          border: `1px solid color-mix(in srgb, ${c} 22%, transparent)`,
          '& .MuiAlert-icon': { color: c },
        };
      }}
    />
  );
}
